/**
 * Talking to /api/v1 as the device rather than as a user.
 *
 * Shares the error envelope and `ApiError` with the operational client, so a
 * caller handles a device failure the same way it handles any other, and only
 * the credential differs: a signature computed per request instead of the
 * session cookie the browser attaches by itself.
 */
import { ApiError, type ApiErrorBody } from "@/app/api-client"
import type { DeviceIdentity } from "./identity"
import { signedHeaders } from "./signing"

export const PROTOCOL_HEADER = "X-ERegister-Protocol"

export const DEVICE_PATHS = {
  enroll: "/api/v1/devices/enroll",
  heartbeat: "/api/v1/devices/heartbeat",
  credential: "/api/v1/devices/credential",
  presence: (stationCode: string) => `/api/v1/stations/${encodeURIComponent(stationCode)}/presence`,
  bootstrap: "/api/v1/bootstrap",
  bootstrapReport: "/api/v1/bootstrap/report",
  snapshot: "/api/v1/snapshot",
  registerSchema: (registerCode: string) =>
    `/api/v1/bootstrap/registers/${encodeURIComponent(registerCode)}/schema`,
  registerEntries: (registerCode: string, cursor: number | null, limit: number) => {
    const query = new URLSearchParams({ limit: String(limit) })
    if (cursor !== null) query.set("cursor", String(cursor))

    return `/api/v1/bootstrap/registers/${encodeURIComponent(registerCode)}/entries?${query.toString()}`
  }
} as const

/**
 * Read from the layout rather than compiled in, so the protocol the client
 * claims and the one the server supports cannot drift apart across a deploy
 * where the bundle is cached and the server is not.
 */
export function protocolVersion(document: Document): string {
  return document.querySelector<HTMLMetaElement>('meta[name="eregister-protocol"]')?.content ?? "1"
}

export function csrfToken(document: Document): string {
  return document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content ?? ""
}

/**
 * A request signed by the device. The body is serialised once and both signed
 * and sent — signing a different serialisation of the same object is the
 * classic way to make this fail with no clue why.
 */
export async function signedRequest<T>(
  identity: DeviceIdentity,
  method: "GET" | "POST" | "PATCH" | "DELETE",
  path: string,
  payload: unknown = {},
  options: { protocol?: string; clock?: () => number } = {}
): Promise<T> {
  // A GET carries no body, and the server hashes `request.raw_post` — which is
  // the empty string for one. Signing "{}" here and hashing "" there is the sort
  // of mismatch that reports itself as an invalid signature and points at the
  // key rather than at the body that was never sent.
  const sendsBody = method !== "GET"
  const body = sendsBody ? JSON.stringify(payload) : ""

  const headers = await signedHeaders(
    identity.deviceUuid,
    identity.privateKey,
    { method, fullPath: path, body },
    options.clock
  )

  return unwrap<T>(
    await fetch(path, {
      method,
      ...(sendsBody ? { body } : {}),
      headers: {
        ...headers,
        [PROTOCOL_HEADER]: options.protocol ?? protocolVersion(document),
        Accept: "application/json",
        ...(sendsBody ? { "Content-Type": "application/json" } : {})
      }
    })
  )
}

/**
 * For the requests a device signature cannot cover, which go out on the ordinary
 * same-origin session and carry a CSRF token like any other write from a page
 * this application rendered.
 *
 * There are two. Enrollment is made before there is a key to sign with. Minting
 * an offline login code is made from the operator's phone, which was never a
 * device and has nothing to sign with at all.
 */
export async function sessionRequest<T>(path: string, payload: unknown, document: Document): Promise<T> {
  return unwrap<T>(
    await fetch(path, {
      method: "POST",
      credentials: "same-origin",
      body: JSON.stringify(payload),
      headers: {
        [PROTOCOL_HEADER]: protocolVersion(document),
        Accept: "application/json",
        "Content-Type": "application/json",
        "X-CSRF-Token": csrfToken(document)
      }
    })
  )
}

async function unwrap<T>(response: Response): Promise<T> {
  if (response.status === 204) return undefined as T

  const payload = await response.json().catch(() => null)

  if (!response.ok) {
    const body = payload as ApiErrorBody | null
    throw new ApiError(
      response.status,
      body?.error?.code ?? "unknown",
      body?.error?.message ?? response.statusText,
      body?.error?.details ?? {}
    )
  }

  return payload as T
}
